import type { z } from 'zod'

export type Tab = {
  label: string
  icon?: string | object
  key?: string
  disabled?: boolean
  current?: boolean
  hidden?: boolean
  mode?: 'query' | 'scroll'
}

function tabMap(obj: Record<string, Tab>, fn: (tab: Tab, key?: string) => Tab) {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, fn(value, key)]),
  )
}

export default function<T extends z.ZodTypeAny>(
  viewSchema: T,
  defaultTabKey: z.TypeOf<T>,
  tabs: MaybeRef<Record<z.TypeOf<T>, Tab>>,
  mode: 'query' | 'scroll' = 'query',
) {
  const route = useRoute()
  const view = computed<z.TypeOf<T>>(() => {
    const parsedView = viewSchema.safeParse(route.query.view)
    if (parsedView.success) {
      return parsedView.data
    }
    return defaultTabKey
  })

  const renderedTabs = computed<Record<string, Tab>>(() => tabMap(unref(tabs), (tab, key) => ({
    ...tab,
    key,
    current: (key === view.value) && mode === 'query',
    mode,
  })))

  return {
    view,
    tabs: renderedTabs,
    defaultTabKey,
  }
}
